import { CreateLead } from '../../Models/createLead';
import { DatosPlanes } from '../../Models/datosPlanes';
import { LeadResponse } from '../../Models/leadResponse';
import { RemainingDataFormInterface } from '../../motos/interfaces/remainingDataForm.interface';
import { RetakeData } from './retake.models';

/**
 * Maps the data from the retake endpoint to a partial CreateLead object (for motos).
 * This is used to pre-fill the form on the cotizador page.
 * @param retakeData The data object from the RetakeResponse.
 * @returns A partial CreateLead object.
 */
/**
 * Maps the data from the retake endpoint to a partial CreateLead object (for motos).
 * This is used to populate the initial form data (Create Lead context).
 * @param retakeData The data object from the RetakeResponse.
 * @returns A partial CreateLead object with Payer and Vehicle info.
 */
export function mapRetakeDataToLeadRequest(retakeData: RetakeData): Partial<CreateLead> {
  const rs = retakeData; // Shortcut
  const payer = rs.payer;
  const veh = rs.vehiculo?.vehiculo;
  const aseg = rs.asegurado?.Asegurado;

  // 1. Construct Name (Prioritize Payer -> Asegurado)
  let leadName = '';
  if (payer?.firstname || payer?.lastname) {
    leadName = [
      payer.firstname,
      payer.middlename,
      payer.lastname,
      payer.SecondLastName
    ].filter(p => p && p.trim()).join(' ');
  } else if (aseg?.NombreCompleto) {
    leadName = aseg.NombreCompleto;
  }

  // 2. Map Payer Address & Contact
  const leadRequest: Partial<CreateLead> = {
    assessor_code: 0, 
    name: leadName,
    mobile_phone: payer?.mobile_phone || payer?.phone || aseg?.NumeroCelular || '',
    email: payer?.email || '',
    phone: payer?.phone || '',

    // Document
    document: {
      type: payer?.DocumentType || aseg?.TipoDocumento || '',
      number: payer?.DocumentNumber || aseg?.NumeroDocumento || '',
      issued: payer?.DocumentExpedition || '',
    },

    // Birth Date
    owner_borndate: payer?.born_date || aseg?.FechaNacimiento || undefined,

    // Address (Payer)
    address: {
      country: payer?.country || 'CO',
      department_id: payer?.department_id || '',
      city_id: payer?.city_id || '',
      street: payer?.street || '',
    },

    // Vehicle Basic Info
    plate: payer?.Placa || veh?.Placa || '',
    confirm_plate: payer?.Placa || veh?.Placa || '',
    use_type: veh?.UsoVehiculo || veh?.TipoDeUso || 'particular',

    // Circulation Zone (Logic: Vehicle ID -> Payer City ID -> Payer Zone String if numeric)
    circulation_zone_id: veh?.ZonaCirculacionId || payer?.city_id || (payer?.zonaCirculacion && /^\d+$/.test(payer.zonaCirculacion) ? payer.zonaCirculacion : '') || '',

    // City Name (Logic: Vehicle City -> Payer Zone Name)
    cityName: veh?.CiudadCirculacion || (payer?.zonaCirculacion && !/^\d+$/.test(payer.zonaCirculacion) ? payer.zonaCirculacion : '') || undefined,

    accept_tos: true,
  };

  return leadRequest;
}


/**
 * Maps the data from the retake endpoint to a LeadResponse object (for motos).
 * This recreates the "RUNT Query Result" state in the Store.
 * @param retakeData The data object from the RetakeResponse.
 * @returns A populated LeadResponse object.
 */
export function mapRetakeDataToLeadResponse(retakeData: RetakeData): LeadResponse {
  const rs = retakeData;
  const veh = rs.vehiculo?.vehiculo;
  const payer = rs.payer;
  const aseg = rs.asegurado?.Asegurado;

  // Determine Prenda status
  // Logic: Check 'Prenda' (e.g. "Si"/"No") OR 'PrendarioRunt' existence
  const isPrenda = (veh?.Prenda === 'Si' || veh?.Prenda === 'SI' || (veh?.PrendaRunt && veh.PrendaRunt !== 'NO')) ? 'SI' : 'NO';
  const prendario = veh?.PrendarioRunt || veh?.PrendaRunt || '';

  // Construct Owner Name
  const ownerFirstnames = payer?.firstname ? `${payer.firstname} ${payer.middlename || ''}`.trim() : aseg?.PrimerNombre || ''; 
  const ownerLastnames = payer?.lastname ? `${payer.lastname} ${payer.SecondLastName || ''}`.trim() : `${aseg?.PrimerApellido || ''} ${aseg?.SegundoApellido || ''}`.trim();

  return {
    additionalInfo: {
      isPrenda: isPrenda,
      prendario: prendario,
    },
    data: {
      Id: payer?.lead_id || '',
      lead: {
        accept_tos: true,
        mobile_phone: payer?.mobile_phone || payer?.phone || aseg?.NumeroCelular || '',
        owner_borndate: payer?.born_date || aseg?.FechaNacimiento || '',
        email: payer?.email || '',
        address: {
          city_id: payer?.city_id || '',
          department_id: payer?.department_id || '',
          street: payer?.street || '',
        }
      },
      vehicle: {
        // Core RUNT Data
        plate: veh?.Placa || payer?.Placa || '',
        model: veh?.Modelo || '',
        brand: veh?.Marca || '',
        cylinder: veh?.Cilindraje || '',
        line: veh?.Linea || '', // "GPD155-A (NMAX155)"
        
        // Technical Details
        engine: veh?.Motor || '',
        chassis: veh?.Chasis || '',
        class: veh?.Clase || '', // "10"
        code_dane: veh?.CodDane || '',
        code_fasecolda: veh?.CodFasecolda || '',
        code_brand_line: veh?.CodigoMarcaLinea || '',
        combustion_type: veh?.Combustible || veh?.TipoCombustible || '', // "GASOLINA"
        
        // Attributes
        blindado: veh?.Blindado === 'true',
        electrico: veh?.Electrico === 'true',
        potencia: veh?.Potencia || '',
        
        // Status & Location
        circulation_zone_id: veh?.ZonaCirculacionId || payer?.city_id || '', 
        circulation_zone: veh?.ZonaCirculacion || veh?.CiudadCirculacion || '',
        
        // Commercial & Legal
        Prenda: isPrenda,
        use_type: veh?.UsoVehiculo || veh?.TipoDeUso || 'particular', 
        service_type: veh?.TipoDeUso || 'Particular',
        
        // Values
        insurable_value: veh?.ValorAsegurado || 0,
        insurable_value_runt: veh?.ValorAseguradoRunt || 0,
        
        owner: {
          document: {
            number: payer?.DocumentNumber || aseg?.NumeroDocumento || '',
            type: payer?.DocumentType || aseg?.TipoDocumento || '',
            issued: payer?.DocumentExpedition || '',
          },
          firstnames: ownerFirstnames,
          lastnames: ownerLastnames,
          fullname: `${ownerFirstnames} ${ownerLastnames}`.trim() || aseg?.NombreCompleto || '',
        },
      },
    },
    error: 0,
    errorCode: '',
    message: 'Retake successful - Full Data Mapped',
  };
}

/**
 * Maps the data from the retake endpoint to a RemainingDataFormInterface object.
 * This is used to pre-fill the Datos-Restantes form when resuming a session.
 * @param retakeData The data object from the RetakeResponse.
 * @returns A partial RemainingDataFormInterface object with available data.
 */
export function mapRetakeDataToRemainingForm(retakeData: RetakeData): Partial<RemainingDataFormInterface> {
  const payer = retakeData.payer;
  const aseg = retakeData.asegurado?.Asegurado;

  // Determine if payer is the owner based on available data
  // If payer has document info and it's different from asegurado, then payer is different
  const payerDoc = payer?.DocumentNumber || '';
  const asegDoc = aseg?.NumeroDocumento || '';
  const isPayer: 'Y' | 'N' = (!payerDoc || payerDoc === asegDoc) ? 'Y' : 'N';

  // Build city string - prefer zonaCirculacion label if available, otherwise build from IDs
  // The dropdown expects format like "Bogotá D.C. - Cundinamarca" (label from circulation_zones)
  const ownerCityId = payer?.owner_address_city_id || payer?.city_id || '';
  const ownerDeptId = payer?.owner_address_department_id || payer?.department_id || '';
  // Use zonaCirculacion if it looks like a label (contains text, not just numbers)
  const zonaCirculacionLabel = payer?.zonaCirculacion && !/^\d+$/.test(payer.zonaCirculacion)
    ? payer.zonaCirculacion
    : '';
  const ownerCity = zonaCirculacionLabel || (ownerCityId && ownerDeptId ? `${ownerCityId} - ${ownerDeptId}` : '');

  const payerCityId = payer?.payer_residency_city_id || payer?.city_id || '';
  const payerDeptId = payer?.payer_residency_department_id || payer?.department_id || '';
  const payerCity = zonaCirculacionLabel || (payerCityId && payerDeptId ? `${payerCityId} - ${payerDeptId}` : '');

  return {
    // Owner/Asegurado data
    bornDate: payer?.owner_born_date || payer?.born_date || aseg?.FechaNacimiento || '',
    expeditionDate: payer?.owner_document_issued || payer?.DocumentExpedition || '',
    // Pass country code as-is; the form component will map it to label using countriesFullData
    country: payer?.owner_address_country || payer?.country || 'CO',
    city: ownerCity,
    address: payer?.owner_address_street || payer?.street || '',
    number: payer?.owner_phone || payer?.mobile_phone || payer?.phone || aseg?.NumeroCelular || '',
    email: payer?.owner_email || payer?.email || '',
    termsAndConditions: true,
    isPayer: isPayer,

    // Payer data (when different from owner)
    payerFirstName: payer?.firstname || '',
    payerSecondName: payer?.middlename || '',
    payerFirstLastName: payer?.lastname || '',
    payerSecondLastName: payer?.SecondLastName || '',
    payerIdentificationType: payer?.DocumentType || '',
    payerDocument: payer?.DocumentNumber || '',
    payerExpeditionDate: payer?.DocumentExpedition || '',
    payerBornDate: payer?.born_date || '',
    // Pass country code as-is; the form component will map it to label using countriesFullData
    payerCountry: payer?.payer_residency_country || payer?.country || 'CO',
    payerCity: payerCity,
    payerAddress: payer?.payer_residency_street || payer?.street || '',
    payerNumber: payer?.phone || payer?.mobile_phone || '',
    payerEmail: payer?.email || '',
  };
}

/**
 * Maps the data from the retake endpoint to a partial DatosPlanes object.
 * This is used to restore the selected plan when resuming a session.
 * @param retakeData The data object from the RetakeResponse.
 * @returns A partial DatosPlanes object with selected_plan and payment_frequency, or null if no plan data.
 */
export function mapRetakeDataToPlanesSelection(retakeData: RetakeData): Partial<DatosPlanes> | null {
  const payer = retakeData.payer;

  // Si no hay plan seleccionado, retornar null
  if (!payer?.Plan) {
    console.log('ℹ️ No hay plan seleccionado en los datos de retoma');
    return null;
  }

  // Usar frecuencia de pago del backend si está disponible, sino usar "Annually" por defecto
  const payment_type = payer.payment_type || 'Annually';

  console.log('📋 Mapeando plan seleccionado desde retoma:', {
    plan: payer.Plan,
    paymentFrequency: payment_type
  });

  return {
    lead_id: payer.lead_id || '',
    selected_plan: payer.Plan,
    payment_frequency: payment_type,
    // Dejar planes vacío - se llenará cuando se consulten las tarifas
    planes: {
      data: {
        products: []
      },
      FalloTarifaEsencialMotos: false,
      FalloTarifaTotalMotos: false
    }
  };
}
